import React, { useEffect, useMemo, useState } from 'react';
import { PackageIcon, PlusIcon, SearchIcon, EditIcon, TrashIcon, ShoppingCartIcon, CalendarIcon, UserIcon, TagIcon, DollarSignIcon, ArchiveIcon, ArchiveRestoreIcon, AlertTriangleIcon, BarChart2Icon, TrendingUpIcon, TrendingDownIcon, BoxIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { FormSection } from '../ui/FormSection';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { Product, ProductSale, ProductSaleItem } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { supabase } from '../../utils/supabase';
// Helper to normalize names for comparison
function normalizeName(name: string) {
  if (!name) return '';
  return name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^(dr\.|dra\.|lic\.|ing\.|ter\.)\s*/g, '').replace(/\s+/g, ' ');
}
const CATEGORIES = [{
  value: 'Cuidado Facial',
  label: 'Cuidado Facial'
}, {
  value: 'Cuidado Corporal',
  label: 'Cuidado Corporal'
}, {
  value: 'Suplementos',
  label: 'Suplementos'
}, {
  value: 'Equipos',
  label: 'Equipos'
}, {
  value: 'Accesorios',
  label: 'Accesorios'
}, {
  value: 'Otro',
  label: 'Otro'
}];

// ─── helpers: Supabase row → app type ────────────────────────────────────────
function rowToProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku,
    description: row.description ?? '',
    price: Number(row.price),
    cost: Number(row.cost),
    stock: row.stock,
    category: row.category,
    status: row.is_archived ? 'archived' as any : row.status,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  };
}
function rowToSale(row: any): ProductSale {
  return {
    id: row.id,
    date: row.date,
    patientId: row.patient_id ?? '',
    items: row.items as ProductSaleItem[],
    total: Number(row.total),
    notes: row.notes ?? '',
    createdByUserId: row.created_by_user_id,
    createdByUserName: row.created_by_user_name,
    createdAt: new Date(row.created_at)
  };
}
export function Products() {
  const {
    patients,
    addAuditLog
  } = useData();
  const {
    user
  } = useAuth();
  const {
    addNotification
  } = useNotifications();
  const {
    isAdmin,
    isTherapist,
    getPermissionLevel
  } = useRolePermissions();
  const canEditSales = isAdmin || isTherapist;
  const statsPermLevel = isAdmin ? 'full' : getPermissionLevel('products_stats') ?? 'none';
  const canViewProductStats = statsPermLevel === 'full' || statsPermLevel === 'read';
  const [activeTab, setActiveTab] = useState('catalog');
  const [loading, setLoading] = useState(true);
  // Data State
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [sellers, setSellers] = useState<{
    id: string;
    name: string;
  }[]>([]);
  // ─── Load Data from Supabase ─────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [{
          data: productsData,
          error: productsError
        }, {
          data: salesData,
          error: salesError
        }, {
          data: usersData
        }, {
          data: doctorsData
        }] = await Promise.all([supabase.from('products').select('*').order('created_at', {
          ascending: false
        }), supabase.from('product_sales').select('*').order('date', {
          ascending: false
        }), supabase.from('users').select('id, name, role'), supabase.from('doctors').select('id, name')]);
        if (productsError) throw productsError;
        if (salesError) throw salesError;
        setProducts((productsData ?? []).map(rowToProduct));
        setSales((salesData ?? []).map(rowToSale));
        // Build sellers list: users with allowed roles + doctors, deduplicated by name
        const allowedRoles = new Set(['doctor', 'doctora', 'terapeuta', 'terapista', 'recepcion', 'recepción', 'recepcionista', 'callcenter', 'call center', 'call_center']);
        const sellersList: {
          id: string;
          name: string;
        }[] = [];
        const seenNormalizedNames = new Set<string>();
        (usersData ?? []).forEach((u: any) => {
          if (!allowedRoles.has((u.role || '').toLowerCase())) return;
          const norm = normalizeName(u.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: u.id,
              name: u.name
            });
          }
        });
        (doctorsData ?? []).forEach((d: any) => {
          const norm = normalizeName(d.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: d.id,
              name: d.name
            });
          }
        });
        setSellers(sellersList);
      } catch (e) {
        console.error('Error loading data from Supabase:', e);
        addNotification({
          type: 'error',
          title: 'Error de carga',
          message: 'No se pudieron cargar los productos desde la base de datos.'
        });
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);
  // ─── CATALOG TAB STATE ───────────────────────────────────────────────────
  const [productSearch, setProductSearch] = useState('');
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    cost: '',
    stock: '',
    category: 'Cuidado Facial',
    status: 'active' as 'active' | 'inactive'
  });
  const [productErrors, setProductErrors] = useState<Record<string, string>>({});
  const [archiveConfirmProduct, setArchiveConfirmProduct] = useState<Product | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const filteredProducts = products.filter(p => p.status as any !== 'archived' && (p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase()) || p.category.toLowerCase().includes(productSearch.toLowerCase())));
  const archivedProducts = products.filter(p => p.status as any === 'archived');

  /* ── Stats computations ─────────────────────────────────────────────── */
  const [statsDateFrom, setStatsDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [statsDateTo, setStatsDateTo] = useState<string>(getToday());
  const [selectedStatProducts, setSelectedStatProducts] = useState<string[]>([]); // empty = all
  const [productSelectorSearch, setProductSelectorSearch] = useState('');
  const productStats = useMemo(() => {
    const filtered = sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo));
    const map: Record<string, {
      id: string;
      name: string;
      category: string;
      revenue: number;
      units: number;
      salesCount: number;
      cost: number;
    }> = {};
    filtered.forEach(sale => {
      sale.items.forEach((item: any) => {
        // If products are selected, only include those
        if (selectedStatProducts.length > 0 && !selectedStatProducts.includes(item.productId)) return;
        const prod = products.find(p => p.id === item.productId);
        const id = item.productId;
        const name = prod?.name ?? item.productName ?? 'Desconocido';
        const cat = prod?.category ?? '—';
        const cost = (prod?.cost ?? 0) * Number(item.quantity ?? 0);
        if (!map[id]) map[id] = {
          id,
          name,
          category: cat,
          revenue: 0,
          units: 0,
          salesCount: 0,
          cost: 0
        };
        map[id].revenue += Number(item.subtotal ?? 0);
        map[id].units += Number(item.quantity ?? 0);
        map[id].salesCount += 1;
        map[id].cost += cost;
      });
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [sales, products, statsDateFrom, statsDateTo, selectedStatProducts]);
  const totalStatsRevenue = productStats.reduce((s, p) => s + p.revenue, 0);
  const totalStatsUnits = productStats.reduce((s, p) => s + p.units, 0);
  const totalStatsProfit = productStats.reduce((s, p) => s + (p.revenue - p.cost), 0);

  // Revenue trend by month (last months in range)
  const revenueTrend = useMemo(() => {
    const months: Record<string, number> = {};
    sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo)).forEach(sale => {
      const key = sale.date.slice(0, 7); // YYYY-MM
      months[key] = (months[key] || 0) + sale.total;
    });
    return Object.entries(months).sort(([a], [b]) => a.localeCompare(b)).map(([month, amount]) => ({
      month: new Date(month + '-01').toLocaleDateString('es', {
        month: 'short',
        year: '2-digit'
      }),
      amount
    }));
  }, [sales, statsDateFrom, statsDateTo]);
  const {
    sortedItems: sortedProducts,
    requestSort: requestProductSort,
    isSorted: isProductSorted
  } = useSortableData(filteredProducts);
  const {
    paginatedItems: paginatedProducts,
    currentPage: productPage,
    totalPages: productTotalPages,
    totalItems: productTotalItems,
    startIndex: productStartIndex,
    endIndex: productEndIndex,
    goToPage: productGoToPage,
    nextPage: productNextPage,
    prevPage: productPrevPage
  } = usePagination(sortedProducts);
  const handleOpenProductModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        sku: product.sku,
        description: product.description,
        price: product.price.toString(),
        cost: product.cost.toString(),
        stock: product.stock.toString(),
        category: product.category,
        status: product.status
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        sku: '',
        description: '',
        price: '',
        cost: '',
        stock: '',
        category: 'Cuidado Facial',
        status: 'active'
      });
    }
    setProductErrors({});
    setIsProductModalOpen(true);
  };
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!productForm.name.trim()) errors.name = 'El nombre es requerido';
    if (!productForm.sku.trim()) errors.sku = 'El SKU es requerido';
    if (!productForm.price || isNaN(Number(productForm.price))) errors.price = 'Precio inválido';
    if (!productForm.cost || isNaN(Number(productForm.cost))) errors.cost = 'Costo inválido';
    if (!productForm.stock || isNaN(Number(productForm.stock))) errors.stock = 'Stock inválido';
    if (Object.keys(errors).length > 0) {
      setProductErrors(errors);
      return;
    }
    const payload = {
      name: productForm.name,
      sku: productForm.sku,
      description: productForm.description,
      price: Number(productForm.price),
      cost: Number(productForm.cost),
      stock: Number(productForm.stock),
      category: productForm.category,
      status: productForm.status
    };
    try {
      if (editingProduct) {
        const {
          data,
          error
        } = await supabase.from('products').update(payload).eq('id', editingProduct.id).select().single();
        if (error) throw error;
        setProducts(products.map(p => p.id === editingProduct.id ? rowToProduct(data) : p));
        addNotification({
          type: 'success',
          title: 'Producto Actualizado',
          message: `${productForm.name} ha sido actualizado.`
        });
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'update',
          module: 'products',
          entityId: editingProduct.id,
          entityName: productForm.name,
          details: `Producto actualizado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
        });
      } else {
        const {
          data,
          error
        } = await supabase.from('products').insert(payload).select().single();
        if (error) throw error;
        setProducts([rowToProduct(data), ...products]);
        addNotification({
          type: 'success',
          title: 'Producto Creado',
          message: `${productForm.name} ha sido agregado al catálogo.`
        });
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'create',
          module: 'products',
          entityId: data.id,
          entityName: productForm.name,
          details: `Producto creado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
        });
      }
      setIsProductModalOpen(false);
    } catch (e: any) {
      console.error('Error saving product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo guardar el producto.'
      });
    }
  };
  const handleArchiveProduct = (id: string) => {
    const product = products.find(p => p.id === id);
    if (product) setArchiveConfirmProduct(product);
  };
  const confirmArchiveProduct = async () => {
    if (!archiveConfirmProduct) return;
    setArchiveLoading(true);
    try {
      const {
        error
      } = await supabase.from('products').update({
        is_archived: true
      }).eq('id', archiveConfirmProduct.id);
      if (error) throw error;
      setProducts(products.map(p => p.id === archiveConfirmProduct.id ? {
        ...p,
        status: 'archived' as any
      } : p));
      addNotification({
        type: 'success',
        title: 'Producto Archivado',
        message: `"${archiveConfirmProduct.name}" fue archivado. Puedes restaurarlo desde Productos Archivados.`
      });
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'update',
        module: 'products',
        entityId: archiveConfirmProduct.id,
        entityName: archiveConfirmProduct.name,
        details: `Producto archivado: ${archiveConfirmProduct.name} (SKU: ${archiveConfirmProduct.sku || 'N/A'})`
      });
      setArchiveConfirmProduct(null);
    } catch (e: any) {
      console.error('Error archiving product:', e);
      addNotification({
        type: 'error',
        title: 'Error al archivar',
        message: e.message ?? 'No se pudo archivar el producto.'
      });
    } finally {
      setArchiveLoading(false);
    }
  };
  const handleUnarchiveProduct = async (id: string) => {
    try {
      const product = products.find(p => p.id === id);
      const {
        error
      } = await supabase.from('products').update({
        is_archived: false
      }).eq('id', id);
      if (error) throw error;
      setProducts(products.map(p => p.id === id ? {
        ...p,
        status: 'active' as any
      } : p));
      addNotification({
        type: 'success',
        title: 'Producto Restaurado',
        message: `"${product?.name}" ha sido restaurado al catálogo.`
      });
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'update',
        module: 'products',
        entityId: id,
        entityName: product?.name || 'Producto',
        details: `Producto restaurado del archivo: ${product?.name || 'Producto'} (SKU: ${product?.sku || 'N/A'})`
      });
    } catch (e: any) {
      console.error('Error unarchiving product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo restaurar el producto.'
      });
    }
  };
  // ─── SALES TAB STATE ─────────────────────────────────────────────────────
  const [saleSearch, setSaleSearch] = useState('');
  const [saleDateFrom, setSaleDateFrom] = useState('');
  const [saleDateTo, setSaleDateTo] = useState('');
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [editingSale, setEditingSale] = useState<ProductSale | null>(null);
  const [saleForm, setSaleForm] = useState({
    patientId: '',
    sellerId: '',
    date: getToday(),
    notes: ''
  });
  const [saleItems, setSaleItems] = useState<{
    productId: string;
    quantity: number;
    discount: number;
  }[]>([]);
  const [saleErrors, setSaleErrors] = useState<Record<string, string>>({});
  const filteredSales = sales.filter(s => {
    const patient = patients.find(p => p.id === s.patientId);
    const matchesSearch = !saleSearch || patient?.name.toLowerCase().includes(saleSearch.toLowerCase()) || s.createdByUserName.toLowerCase().includes(saleSearch.toLowerCase());
    const matchesDateFrom = !saleDateFrom || s.date >= saleDateFrom;
    const matchesDateTo = !saleDateTo || s.date <= saleDateTo;
    return matchesSearch && matchesDateFrom && matchesDateTo;
  });
  const {
    sortedItems: sortedSales,
    requestSort: requestSaleSort,
    isSorted: isSaleSorted
  } = useSortableData(filteredSales);
  const {
    paginatedItems: paginatedSales,
    currentPage: salePage,
    totalPages: saleTotalPages,
    totalItems: saleTotalItems,
    startIndex: saleStartIndex,
    endIndex: saleEndIndex,
    goToPage: saleGoToPage,
    nextPage: saleNextPage,
    prevPage: salePrevPage
  } = usePagination(sortedSales);
  const handleOpenSaleModal = () => {
    setEditingSale(null);
    setSaleForm({
      patientId: '',
      sellerId: '',
      date: getToday(),
      notes: ''
    });
    setSaleItems([{
      productId: '',
      quantity: 1,
      discount: 0
    }]);
    setSaleErrors({});
    setIsSaleModalOpen(true);
  };
  const handleOpenEditSaleModal = (sale: ProductSale) => {
    setEditingSale(sale);
    setSaleForm({
      patientId: sale.patientId || '',
      sellerId: sale.createdByUserId || '',
      date: sale.date,
      notes: sale.notes || ''
    });
    setSaleItems(sale.items.map((item: any) => ({
      productId: item.productId,
      quantity: item.quantity,
      discount: item.discount || 0
    })));
    setSaleErrors({});
    setIsSaleModalOpen(true);
  };
  const addSaleItem = () => setSaleItems([...saleItems, {
    productId: '',
    quantity: 1,
    discount: 0
  }]);
  const removeSaleItem = (index: number) => setSaleItems(saleItems.filter((_, i) => i !== index));
  const updateSaleItem = (index: number, field: 'productId' | 'quantity' | 'discount', value: any) => {
    const newItems = [...saleItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    setSaleItems(newItems);
    // Clear any row-specific or global "items" error so stale messages don't linger
    setSaleErrors(prev => {
      if (!prev[`item_${index}`] && !prev.items) return prev;
      const next = {
        ...prev
      };
      delete next[`item_${index}`];
      delete next.items;
      return next;
    });
  };
  const calculateSaleTotal = () => saleItems.reduce((total, item) => {
    if (!item.productId) return total;
    const product = products.find(p => p.id === item.productId);
    if (!product) return total;
    const lineTotal = product.price * (item.quantity || 0);
    const discountAmount = lineTotal * ((item.discount || 0) / 100);
    return total + lineTotal - discountAmount;
  }, 0);
  const handleSaveSale = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!saleForm.patientId) errors.patientId = 'Debe seleccionar un cliente/paciente';
    if (!saleForm.sellerId) errors.sellerId = 'Debe seleccionar un vendedor';
    if (!saleForm.date) errors.date = 'La fecha es requerida';
    const validItems = saleItems.filter(item => item.productId && item.quantity > 0);
    if (validItems.length === 0) errors.items = 'Debe agregar al menos un producto válido';
    // For new sales only: check stock. For edits, we check net difference.
    if (!editingSale) {
      validItems.forEach((item, idx) => {
        const product = products.find(p => p.id === item.productId);
        if (product && product.stock < item.quantity) errors[`item_${idx}`] = `Stock insuficiente (${product.stock} disponibles)`;
      });
    } else {
      // For edits: check stock considering items returned from old sale
      validItems.forEach((item, idx) => {
        const product = products.find(p => p.id === item.productId);
        if (!product) return;
        const oldItem = editingSale.items.find((oi: any) => oi.productId === item.productId);
        const returnedQty = oldItem ? (oldItem as any).quantity : 0;
        const availableStock = product.stock + returnedQty;
        if (availableStock < item.quantity) errors[`item_${idx}`] = `Stock insuficiente (${availableStock} disponibles tras devolver la venta anterior)`;
      });
    }
    if (Object.keys(errors).length > 0) {
      setSaleErrors(errors);
      return;
    }
    const finalItems: ProductSaleItem[] = validItems.map(item => {
      const product = products.find(p => p.id === item.productId)!;
      const lineTotal = product.price * item.quantity;
      const discountAmount = lineTotal * ((item.discount || 0) / 100);
      return {
        productId: item.productId,
        productName: product.name,
        quantity: item.quantity,
        unitPrice: product.price,
        discount: item.discount || 0,
        subtotal: lineTotal - discountAmount
      };
    });
    const total = finalItems.reduce((sum, item) => sum + item.subtotal, 0);
    const selectedSeller = sellers.find(s => s.id === saleForm.sellerId);
    const sellerUserId = selectedSeller?.id ?? user?.id ?? 'unknown';
    const sellerUserName = selectedSeller?.name ?? user?.name ?? 'Usuario';
    try {
      if (editingSale) {
        // ── EDIT FLOW ──────────────────────────────────────────────────────
        const {
          error: updateError
        } = await supabase.from('product_sales').update({
          date: saleForm.date,
          patient_id: saleForm.patientId || null,
          items: finalItems,
          total,
          notes: saleForm.notes,
          created_by_user_id: sellerUserId,
          created_by_user_name: sellerUserName
        }).eq('id', editingSale.id);
        if (updateError) throw updateError;

        // Restore stock for old items, then deduct for new items
        const stockUpdates: Record<string, number> = {};
        // +restore old quantities
        (editingSale.items as any[]).forEach(oldItem => {
          stockUpdates[oldItem.productId] = (stockUpdates[oldItem.productId] || 0) + oldItem.quantity;
        });
        // -deduct new quantities
        finalItems.forEach(newItem => {
          stockUpdates[newItem.productId] = (stockUpdates[newItem.productId] || 0) - newItem.quantity;
        });
        await Promise.all(Object.entries(stockUpdates).map(([productId, delta]) => {
          const product = products.find(p => p.id === productId);
          if (!product) return Promise.resolve();
          return supabase.from('products').update({
            stock: product.stock + delta
          }).eq('id', productId);
        }));
        // Update local state
        setSales(sales.map(s => s.id === editingSale.id ? {
          ...s,
          date: saleForm.date,
          patientId: saleForm.patientId,
          items: finalItems,
          total,
          notes: saleForm.notes,
          createdByUserId: sellerUserId,
          createdByUserName: sellerUserName
        } : s));
        setProducts(products.map(p => {
          const delta = stockUpdates[p.id];
          return delta !== undefined ? {
            ...p,
            stock: p.stock + delta
          } : p;
        }));
        addNotification({
          type: 'success',
          title: 'Venta Actualizada',
          message: `Venta por ${formatCurrency(total)} actualizada exitosamente.`
        });
        const patientName = patients.find(p => p.id === saleForm.patientId)?.name || 'Cliente';
        const itemsSummary = finalItems.map(item => {
          const base = `${item.quantity}x ${item.productName}`;
          return item.discount ? `${base} (-${item.discount}% desc.)` : base;
        }).join(', ');
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'update',
          module: 'product_sales',
          entityId: editingSale.id,
          entityName: `Venta a ${patientName}`,
          details: `Venta EDITADA por ${user?.name || 'Usuario'}: ${itemsSummary} - Total: ${formatCurrency(total)} - Cliente: ${patientName} - Vendedor: ${sellerUserName}`
        });
      } else {
        // ── CREATE FLOW ────────────────────────────────────────────────────
        const {
          data: saleData,
          error: saleError
        } = await supabase.from('product_sales').insert({
          date: saleForm.date,
          patient_id: saleForm.patientId || null,
          items: finalItems,
          total,
          notes: saleForm.notes,
          created_by_user_id: sellerUserId,
          created_by_user_name: sellerUserName
        }).select().single();
        if (saleError) throw saleError;
        // Update stock for each product sold
        await Promise.all(finalItems.map(item => {
          const product = products.find(p => p.id === item.productId)!;
          return supabase.from('products').update({
            stock: product.stock - item.quantity
          }).eq('id', item.productId);
        }));
        setSales([rowToSale(saleData), ...sales]);
        setProducts(products.map(p => {
          const soldItem = finalItems.find(item => item.productId === p.id);
          return soldItem ? {
            ...p,
            stock: p.stock - soldItem.quantity
          } : p;
        }));
        addNotification({
          type: 'success',
          title: 'Venta Registrada',
          message: `Venta por ${formatCurrency(total)} registrada exitosamente.`
        });
        const patientName = patients.find(p => p.id === saleForm.patientId)?.name || 'Cliente';
        const itemsSummary = finalItems.map(item => {
          const base = `${item.quantity}x ${item.productName}`;
          return item.discount ? `${base} (-${item.discount}% desc.)` : base;
        }).join(', ');
        const hasAnyDiscount = finalItems.some(item => (item.discount || 0) > 0);
        const discountNote = hasAnyDiscount ? ` | Descuento aplicado: ${finalItems.filter(item => (item.discount || 0) > 0).map(item => `${item.productName}: ${item.discount}% (${formatCurrency(item.unitPrice * item.quantity - item.subtotal)})`).join(', ')}` : '';
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'create',
          module: 'product_sales',
          entityId: saleData.id,
          entityName: `Venta a ${patientName}`,
          details: `Venta registrada por ${sellerUserName}: ${itemsSummary} - Total: ${formatCurrency(total)} - Cliente: ${patientName}${discountNote}`
        });
      }
      setIsSaleModalOpen(false);
      setEditingSale(null);
    } catch (e: any) {
      console.error('Error saving sale:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo guardar la venta.'
      });
    }
  };
  const tabs = [{
    id: 'catalog',
    label: 'Catálogo de Productos',
    mobileLabel: 'Catálogo',
    icon: <PackageIcon className="w-4 h-4" data-id="element-3658" />
  }, {
    id: 'sales',
    label: 'Ventas de Productos',
    mobileLabel: 'Ventas',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-3659" />
  }, ...(canViewProductStats ? [{
    id: 'stats',
    label: statsPermLevel === 'read' ? 'Estadísticas (solo lectura)' : 'Estadísticas',
    mobileLabel: 'Estadísticas',
    icon: <BarChart2Icon className="w-4 h-4" data-id="element-3660" />
  }] : []), {
    id: 'archived',
    label: 'Productos Archivados',
    mobileLabel: 'Archivados',
    icon: <ArchiveIcon className="w-4 h-4" data-id="element-3661" />
  }];
  return <div className="space-y-6" data-id="element-3662">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-600 via-orange-500 to-rose-500 p-6 text-white shadow-xl" data-id="element-3663">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-3664" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-3665">
          <div data-id="element-3666">
            <div className="flex items-center gap-3 mb-2" data-id="element-3667">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-3668">
                <PackageIcon className="w-6 h-6" data-id="element-3669" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-3670">Productos</h1>
            </div>
            <p className="text-orange-100 max-w-xl" data-id="element-3671">
              Gestión de inventario y ventas de productos
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-3672">
            {activeTab === 'catalog' ? <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3674" />} onClick={() => handleOpenProductModal()} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-3673">
              
                Nuevo Producto
              </Button> : <Button leftIcon={<ShoppingCartIcon className="w-4 h-4" data-id="element-3676" />} onClick={handleOpenSaleModal} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-3675">
              
                Registrar Venta
              </Button>}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-3677" />

      {/* CATALOG TAB */}
      {activeTab === 'catalog' && <Card padding="none" data-id="element-3678">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-3679">
            <div className="relative max-w-md" data-id="element-3680">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3681" />
              <Input placeholder="Buscar por nombre, SKU o categoría..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="pl-10" data-id="element-3682" />
            
            </div>
          </div>

          {/* ── MOBILE CARDS ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3683">
            {paginatedProducts.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm" data-id="element-3684">
                No se encontraron productos
              </div> : paginatedProducts.map(product => <div key={product.id} className="px-4 py-3 flex items-center gap-3" data-id="element-3685">
            
                  <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 flex-shrink-0" data-id="element-3686">
                    <PackageIcon className="w-4 h-4" data-id="element-3687" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3688">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-3689">
                      {product.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3690">
                      {product.category} · SKU: {product.sku}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-3691">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white" data-id="element-3692">
                        {formatCurrency(product.price)}
                      </span>
                      <span className={`text-xs font-medium ${product.stock <= 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`} data-id="element-3693">
                  
                        Stock: {product.stock}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3694">
                    <button onClick={() => handleOpenProductModal(product)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-3695">
                
                      <EditIcon className="w-4 h-4" data-id="element-3696" />
                    </button>
                    <button onClick={() => handleArchiveProduct(product.id)} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar producto" data-id="element-3697">
                
                      <ArchiveIcon className="w-4 h-4" data-id="element-3698" />
                    </button>
                  </div>
                </div>)}
          </div>

          {/* ── DESKTOP TABLE ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-3699">
            <table className="w-full" data-id="element-3700">
              <thead data-id="element-3701">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-3702">
                  <SortableHeader label="Producto" sortKey="name" currentDirection={isProductSorted('name')} onSort={requestProductSort} data-id="element-3703" />
                

                  <SortableHeader label="SKU" sortKey="sku" currentDirection={isProductSorted('sku')} onSort={requestProductSort} className="hidden sm:table-cell" data-id="element-3704" />
                

                  <SortableHeader label="Categoría" sortKey="category" currentDirection={isProductSorted('category')} onSort={requestProductSort} className="hidden sm:table-cell" data-id="element-3705" />
                

                  <SortableHeader label="Precio" sortKey="price" currentDirection={isProductSorted('price')} onSort={requestProductSort} data-id="element-3706" />
                

                  <SortableHeader label="Stock" sortKey="stock" currentDirection={isProductSorted('stock')} onSort={requestProductSort} data-id="element-3707" />
                

                  <SortableHeader label="Estado" sortKey="status" currentDirection={isProductSorted('status')} onSort={requestProductSort} data-id="element-3708" />
                

                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3709">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3710">
                {loading ? <tr data-id="element-3711">
                    <td colSpan={7} className="py-8 text-center text-gray-400" data-id="element-3712">
                      Cargando productos...
                    </td>
                  </tr> : paginatedProducts.length === 0 ? <tr data-id="element-3713">
                    <td colSpan={7} className="py-8 text-center text-gray-500" data-id="element-3714">
                      No se encontraron productos
                    </td>
                  </tr> : paginatedProducts.map(product => <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-3715">
                
                      <td className="py-3 px-4" data-id="element-3716">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-3717">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-3718">
                          {product.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell" data-id="element-3719">
                        {product.sku}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell" data-id="element-3720">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-3721">
                          <TagIcon className="w-3 h-3" data-id="element-3722" />
                          {product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-3723">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="py-3 px-4" data-id="element-3724">
                        <span className={`font-medium ${product.stock <= 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`} data-id="element-3725">
                    
                          {product.stock}
                        </span>
                      </td>
                      <td className="py-3 px-4" data-id="element-3726">
                        <Badge variant={product.status === 'active' ? 'success' : 'default'} data-id="element-3727">
                    
                          {product.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-3728">
                        <div className="flex justify-end gap-2" data-id="element-3729">
                          <button onClick={() => handleOpenProductModal(product)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar producto" data-id="element-3730">
                      
                            <EditIcon className="w-4 h-4" data-id="element-3731" />
                          </button>
                          <button onClick={() => handleArchiveProduct(product.id)} className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Archivar producto" data-id="element-3732">
                      
                            <ArchiveIcon className="w-4 h-4" data-id="element-3733" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3734">
            <Pagination currentPage={productPage} totalPages={productTotalPages} totalItems={productTotalItems} startIndex={productStartIndex} endIndex={productEndIndex} onPageChange={productGoToPage} onNext={productNextPage} onPrev={productPrevPage} data-id="element-3735" />
          
          </div>
        </Card>}

      {/* SALES TAB */}
      {activeTab === 'sales' && <Card padding="none" data-id="element-3736">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row gap-4" data-id="element-3737">
            <div className="relative flex-1" data-id="element-3738">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3739" />
              <Input placeholder="Buscar por cliente o vendedor..." value={saleSearch} onChange={e => setSaleSearch(e.target.value)} className="pl-10" data-id="element-3740" />
            
            </div>
            <div className="flex items-center gap-2" data-id="element-3741">
              <Input type="date" value={saleDateFrom} onChange={e => setSaleDateFrom(e.target.value)} data-id="element-3742" />
            

              <span className="text-gray-400" data-id="element-3743">→</span>
              <Input type="date" value={saleDateTo} onChange={e => setSaleDateTo(e.target.value)} data-id="element-3744" />
            
            </div>
          </div>

          {/* ── MOBILE CARDS VENTAS ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3745">
            {paginatedSales.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm" data-id="element-3746">
                No se encontraron ventas
              </div> : paginatedSales.map(sale => {
          const patient = patients.find(p => p.id === sale.patientId);
          const saleItems = sale.items || [];
          return <div key={sale.id} className="px-4 py-3 flex items-start gap-3" data-id="element-3747">
                
                    <div className="flex-1 min-w-0" data-id="element-3748">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-3749">
                        {patient?.name || sale.patientName || 'Cliente'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3750">
                        {sale.date} · {sale.createdByUserName || ''}
                      </p>
                      <p className="text-xs text-gray-400 truncate" data-id="element-3751">
                        {saleItems.map((i: any) => {
                  const name = i.productName || '';
                  const disc = i.discount ? ` (-${i.discount}%)` : '';
                  return `${i.quantity || 1}x ${name}${disc}`;
                }).join(', ')}
                      </p>
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-1" data-id="element-3752">
                        {formatCurrency(sale.total)}
                      </p>
                    </div>
                    {canEditSales && <button onClick={() => handleOpenEditSaleModal(sale)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors flex-shrink-0" title="Editar venta" data-id="element-3753">
                      <EditIcon className="w-4 h-4" data-id="element-3754" />
                    </button>}
                  </div>;
        })}
          </div>

          {/* ── DESKTOP TABLE VENTAS ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-3755">
            <table className="w-full" data-id="element-3756">
              <thead data-id="element-3757">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-3758">
                  <SortableHeader label="Fecha" sortKey="date" currentDirection={isSaleSorted('date')} onSort={requestSaleSort} data-id="element-3759" />
                

                  <SortableHeader label="Cliente/Paciente" sortKey="patientId" currentDirection={isSaleSorted('patientId')} onSort={requestSaleSort} data-id="element-3760" />
                

                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3761">
                    Productos
                  </th>
                  <SortableHeader label="Total" sortKey="total" currentDirection={isSaleSorted('total')} onSort={requestSaleSort} data-id="element-3762" />
                

                  <SortableHeader label="Vendedor" sortKey="createdByUserName" currentDirection={isSaleSorted('createdByUserName')} onSort={requestSaleSort} data-id="element-3763" />
                
                  {canEditSales && <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3764">
                    Acciones
                  </th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3765">
                {loading ? <tr data-id="element-3766">
                    <td colSpan={canEditSales ? 6 : 5} className="py-8 text-center text-gray-400" data-id="element-3767">
                      Cargando ventas...
                    </td>
                  </tr> : paginatedSales.length === 0 ? <tr data-id="element-3768">
                    <td colSpan={canEditSales ? 6 : 5} className="py-8 text-center text-gray-500" data-id="element-3769">
                      No se encontraron ventas
                    </td>
                  </tr> : paginatedSales.map(sale => {
              const patient = patients.find(p => p.id === sale.patientId);
              return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-3770">
                    
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white" data-id="element-3771">
                          {sale.date}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-3772">
                          {patient?.name || 'Desconocido'}
                        </td>
                        <td className="py-3 px-4" data-id="element-3773">
                          <div className="flex flex-col gap-1" data-id="element-3774">
                            {sale.items.map((item, idx) => {
                      const product = products.find(p => p.id === item.productId);
                      const hasDiscount = (item as any).discount > 0;
                      return <span key={idx} className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3775">
                              
                                  {item.quantity}x{' '}
                                  {product?.name || item.productName || 'Producto eliminado'}
                                  {hasDiscount && <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-medium" data-id="element-3776">
                                      (-{(item as any).discount}%)
                                    </span>}
                                </span>;
                    })}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-bold text-orange-600 dark:text-orange-400" data-id="element-3777">
                          {formatCurrency(sale.total)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 flex items-center gap-1.5 mt-1" data-id="element-3778">
                          <UserIcon className="w-3.5 h-3.5" data-id="element-3779" />
                          {sale.createdByUserName}
                        </td>
                        {canEditSales && <td className="py-3 px-4 text-right" data-id="element-3780">
                          <button onClick={() => handleOpenEditSaleModal(sale)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar venta" data-id="element-3781">
                            <EditIcon className="w-4 h-4" data-id="element-3782" />
                          </button>
                        </td>}
                      </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3783">
            <Pagination currentPage={salePage} totalPages={saleTotalPages} totalItems={saleTotalItems} startIndex={saleStartIndex} endIndex={saleEndIndex} onPageChange={saleGoToPage} onNext={saleNextPage} onPrev={salePrevPage} data-id="element-3784" />
          
          </div>
        </Card>}

      {/* ── STATS TAB ─────────────────────────────────────────────────────── */}
      {activeTab === 'stats' && canViewProductStats && <div className="space-y-6" data-id="element-3785">

          {/* Read-only notice */}
          {statsPermLevel === 'read' && <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300" data-id="element-3786">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-3787">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" data-id="element-3788" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" data-id="element-3789" />
              </svg>
              <span data-id="element-3790">Estás en modo <strong data-id="element-3791">solo lectura</strong>. Puedes consultar las estadísticas pero no modificar datos desde esta vista.</span>
            </div>}

          {/* Filters row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-3792">
            {/* Date filter */}
            <Card className="lg:col-span-1" data-id="element-3793">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1.5" data-id="element-3794">
                <BarChart2Icon className="w-3.5 h-3.5 text-amber-500" data-id="element-3795" /> Período
              </p>
              <div className="flex flex-col gap-2" data-id="element-3796">
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5" data-id="element-3797">
                  <span className="text-xs text-gray-400 w-8 flex-shrink-0" data-id="element-3798">Desde</span>
                  <input type="date" value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)} className="bg-transparent text-sm border-none focus:ring-0 text-gray-700 dark:text-gray-300 flex-1 min-w-0" data-id="element-3799" />
                </div>
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5" data-id="element-3800">
                  <span className="text-xs text-gray-400 w-8 flex-shrink-0" data-id="element-3801">Hasta</span>
                  <input type="date" value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)} className="bg-transparent text-sm border-none focus:ring-0 text-gray-700 dark:text-gray-300 flex-1 min-w-0" data-id="element-3802" />
                </div>
              </div>
            </Card>

            {/* Product multi-selector */}
            <Card className="lg:col-span-2 overflow-hidden" padding="none" data-id="element-3803">
              <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between" data-id="element-3804">
                <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1.5" data-id="element-3805">
                  <PackageIcon className="w-3.5 h-3.5 text-amber-500" data-id="element-3806" />
                  Filtrar productos
                  {selectedStatProducts.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" data-id="element-3807">
                      {selectedStatProducts.length} seleccionado{selectedStatProducts.length > 1 ? 's' : ''}
                    </span>}
                </p>
                <div className="flex items-center gap-2" data-id="element-3808">
                  <button onClick={() => setSelectedStatProducts(products.filter(p => p.status as any !== 'archived').map(p => p.id))} className="text-xs text-amber-600 dark:text-amber-400 hover:underline" data-id="element-3809">
                    Todos
                  </button>
                  <span className="text-gray-300 text-xs" data-id="element-3810">·</span>
                  <button onClick={() => setSelectedStatProducts([])} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:underline" data-id="element-3811">
                    Limpiar
                  </button>
                </div>
              </div>
              {/* Search */}
              <div className="px-3 pt-3 pb-2" data-id="element-3812">
                <div className="relative" data-id="element-3813">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" data-id="element-3814" />
                  <input type="text" placeholder="Buscar producto..." value={productSelectorSearch} onChange={e => setProductSelectorSearch(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400" data-id="element-3815" />
                </div>
              </div>
              {/* Product chips */}
              <div className="px-3 pb-3 max-h-36 overflow-y-auto flex flex-wrap gap-2" data-id="element-3816">
                {products.filter(p => p.status as any !== 'archived').filter(p => p.name.toLowerCase().includes(productSelectorSearch.toLowerCase())).map(p => {
              const selected = selectedStatProducts.includes(p.id);
              return <button key={p.id} onClick={() => setSelectedStatProducts(selected ? selectedStatProducts.filter(id => id !== p.id) : [...selectedStatProducts, p.id])} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${selected ? 'bg-amber-500 border-amber-500 text-white shadow-sm shadow-amber-300/30' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-amber-400 hover:text-amber-600'}`} data-id="element-3817">
                        {selected && <span className="text-[10px]" data-id="element-3818">✓</span>}
                        {p.name}
                      </button>;
            })}
                {products.filter(p => p.status as any !== 'archived').filter(p => p.name.toLowerCase().includes(productSelectorSearch.toLowerCase())).length === 0 && <p className="text-xs text-gray-400 italic py-2" data-id="element-3819">Sin resultados</p>}
              </div>
              <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800" data-id="element-3820">
                <p className="text-[11px] text-gray-400" data-id="element-3821">
                  {selectedStatProducts.length === 0 ? 'Mostrando todos los productos. Selecciona uno o varios para filtrar.' : `Mostrando estadísticas de ${selectedStatProducts.length} producto${selectedStatProducts.length > 1 ? 's' : ''}.`}
                </p>
              </div>
            </Card>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3822">
            {[{
          label: 'Ingresos totales',
          value: formatCurrency(totalStatsRevenue),
          icon: <DollarSignIcon className="w-5 h-5" data-id="element-3823" />,
          color: 'from-amber-500 to-orange-500',
          sub: `${productStats.length} producto${productStats.length !== 1 ? 's' : ''} vendido${productStats.length !== 1 ? 's' : ''}`
        }, {
          label: 'Unidades vendidas',
          value: totalStatsUnits.toLocaleString(),
          icon: <BoxIcon className="w-5 h-5" data-id="element-3824" />,
          color: 'from-blue-500 to-indigo-500',
          sub: `${sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo)).length} ventas`
        }, {
          label: 'Ganancia estimada',
          value: formatCurrency(totalStatsProfit),
          icon: totalStatsProfit >= 0 ? <TrendingUpIcon className="w-5 h-5" data-id="element-3825" /> : <TrendingDownIcon className="w-5 h-5" data-id="element-3826" />,
          color: totalStatsProfit >= 0 ? 'from-emerald-500 to-green-500' : 'from-red-500 to-rose-500',
          sub: 'Ingresos − Costo'
        }].map(kpi => <div key={kpi.label} className="relative overflow-hidden rounded-2xl p-5 text-white shadow-lg" data-id="element-3827">
                <div className={`absolute inset-0 bg-gradient-to-br ${kpi.color}`} data-id="element-3828" />
                <div className="relative z-10" data-id="element-3829">
                  <div className="flex items-center justify-between mb-3" data-id="element-3830">
                    <p className="text-sm font-medium text-white/80" data-id="element-3831">{kpi.label}</p>
                    <div className="p-2 bg-white/20 rounded-xl" data-id="element-3832">{kpi.icon}</div>
                  </div>
                  <p className="text-2xl font-bold tracking-tight" data-id="element-3833">{kpi.value}</p>
                  <p className="text-xs text-white/70 mt-1" data-id="element-3834">{kpi.sub}</p>
                </div>
              </div>)}
          </div>

          {/* Revenue trend */}
          <Card padding="none" className="overflow-hidden" data-id="element-3835">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800" data-id="element-3836">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-3837">
                <TrendingUpIcon className="w-4 h-4 text-amber-500" data-id="element-3838" />
                Tendencia de ingresos mensual
              </h3>
            </div>
            <div className="p-4" data-id="element-3839">
              {revenueTrend.length < 2 ? <div className="py-10 text-center text-sm text-gray-400 italic" data-id="element-3840">Amplía el rango de fechas para ver la tendencia.</div> : <ResponsiveContainer width="100%" height={220} data-id="element-3841">
                  <LineChart data={revenueTrend} margin={{
              left: 0,
              right: 8,
              top: 4
            }} data-id="element-3842">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" data-id="element-3843" />
                    <XAxis dataKey="month" fontSize={12} stroke="#9ca3af" tickLine={false} axisLine={false} data-id="element-3844" />
                    <YAxis fontSize={11} stroke="#9ca3af" tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-3845" />
                    <Tooltip contentStyle={{
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.10)',
                padding: '10px 14px'
              }} formatter={(v: number) => [formatCurrency(v), 'Ingresos']} data-id="element-3846" />
                    <Line type="monotone" dataKey="amount" stroke="#f59e0b" strokeWidth={2.5} dot={{
                fill: '#f59e0b',
                r: 4
              }} activeDot={{
                r: 6
              }} data-id="element-3847" />
                  </LineChart>
                </ResponsiveContainer>}
            </div>
          </Card>

          {/* Bar chart – products */}
          {productStats.length > 0 && <Card padding="none" className="overflow-hidden" data-id="element-3848">
              <div className="p-4 border-b border-gray-100 dark:border-gray-800" data-id="element-3849">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-3850">
                  <BarChart2Icon className="w-4 h-4 text-amber-500" data-id="element-3851" />
                  Ingresos por producto
                </h3>
              </div>
              <div className="p-4" data-id="element-3852">
                <ResponsiveContainer width="100%" height={Math.max(220, productStats.length * 44)} data-id="element-3853">
                  <BarChart data={productStats} layout="vertical" margin={{
              left: 0,
              right: 50
            }} data-id="element-3854">
                    <defs data-id="element-3855">
                      <linearGradient id="prodBarGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-3856">
                        <stop offset="0%" stopColor="#f59e0b" data-id="element-3857" />
                        <stop offset="100%" stopColor="#ea580c" data-id="element-3858" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal vertical={false} data-id="element-3859" />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-3860" />
                    <YAxis dataKey="name" type="category" width={140} fontSize={12} tickLine={false} axisLine={false} tick={{
                fill: '#374151',
                fontWeight: 500
              }} data-id="element-3861" />
                    <Tooltip contentStyle={{
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.10)',
                padding: '10px 14px'
              }} formatter={(v: number) => [formatCurrency(v), 'Ingresos']} data-id="element-3862" />
                    <Bar dataKey="revenue" fill="url(#prodBarGrad)" radius={[0, 6, 6, 0]} label={{
                position: 'right',
                fontSize: 11,
                fill: '#6b7280',
                formatter: (v: number) => formatCurrency(v)
              }} data-id="element-3863" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>}

          {/* Detail table */}
          <Card padding="none" className="overflow-hidden" data-id="element-3864">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between" data-id="element-3865">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-3866">
                <PackageIcon className="w-4 h-4 text-amber-500" data-id="element-3867" />
                Detalle por producto
              </h3>
              <span className="text-xs text-gray-400" data-id="element-3868">{productStats.length} productos</span>
            </div>
            {productStats.length === 0 ? <div className="py-14 text-center text-gray-400 text-sm italic" data-id="element-3869">
                No hay ventas para los filtros seleccionados.
              </div> : <div className="overflow-x-auto" data-id="element-3870">
                <table className="w-full" data-id="element-3871">
                  <thead data-id="element-3872">
                    <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50" data-id="element-3873">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3874">#</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3875">Producto</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-3876">Categoría</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3877">Unidades</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3878">Ventas</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3879">Costo</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3880">Ingresos</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3881">Ganancia</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell" data-id="element-3882">% del total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3883">
                    {productStats.map((p, i) => {
                const profit = p.revenue - p.cost;
                const pct = totalStatsRevenue > 0 ? p.revenue / totalStatsRevenue * 100 : 0;
                return <tr key={p.id} className="hover:bg-amber-50/40 dark:hover:bg-amber-900/10 transition-colors" data-id="element-3884">
                          <td className="py-3 px-4 text-sm text-gray-400 font-medium" data-id="element-3885">{i + 1}</td>
                          <td className="py-3 px-4" data-id="element-3886">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-3887">{p.name}</p>
                          </td>
                          <td className="py-3 px-4 hidden sm:table-cell" data-id="element-3888">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" data-id="element-3889">
                              <TagIcon className="w-3 h-3" data-id="element-3890" />{p.category}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3891">{p.units.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right text-sm text-gray-500" data-id="element-3892">{p.salesCount}</td>
                          <td className="py-3 px-4 text-right text-sm text-gray-500" data-id="element-3893">{formatCurrency(p.cost)}</td>
                          <td className="py-3 px-4 text-right text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-3894">{formatCurrency(p.revenue)}</td>
                          <td className="py-3 px-4 text-right" data-id="element-3895">
                            <span className={`text-sm font-bold ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-3896">
                              {profit >= 0 ? '+' : ''}{formatCurrency(profit)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right hidden md:table-cell" data-id="element-3897">
                            <div className="flex items-center justify-end gap-2" data-id="element-3898">
                              <div className="w-16 bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden" data-id="element-3899">
                                <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" style={{
                          width: `${pct}%`
                        }} data-id="element-3900" />
                              </div>
                              <span className="text-xs text-gray-500 w-9 text-right" data-id="element-3901">{pct.toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>;
              })}
                  </tbody>
                  <tfoot data-id="element-3902">
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-amber-50/50 dark:bg-amber-900/10" data-id="element-3903">
                      <td colSpan={3} className="py-3 px-4 text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-3904">TOTAL</td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-3905">{totalStatsUnits.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-gray-500" data-id="element-3906">{productStats.reduce((s, p) => s + p.salesCount, 0)}</td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-gray-500" data-id="element-3907">{formatCurrency(productStats.reduce((s, p) => s + p.cost, 0))}</td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-3908">{formatCurrency(totalStatsRevenue)}</td>
                      <td className="py-3 px-4 text-right" data-id="element-3909">
                        <span className={`text-sm font-bold ${totalStatsProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-3910">
                          {totalStatsProfit >= 0 ? '+' : ''}{formatCurrency(totalStatsProfit)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-xs text-gray-400 hidden md:table-cell" data-id="element-3911">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>}
          </Card>
        </div>}

      {/* ── ARCHIVED TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'archived' && <Card padding="none" data-id="element-3912">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3" data-id="element-3913">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg" data-id="element-3914">
              <ArchiveIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-3915" />
            </div>
            <div data-id="element-3916">
              <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-3917">Productos Archivados</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3918">
                {archivedProducts.length} producto{archivedProducts.length !== 1 ? 's' : ''} archivado{archivedProducts.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {archivedProducts.length === 0 ? <div className="py-16 text-center" data-id="element-3919">
              <ArchiveIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-3920" />
              <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-3921">No hay productos archivados</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-3922">Los productos que archive aparecerán aquí</p>
            </div> : <div className="overflow-x-auto" data-id="element-3923">
              <table className="w-full" data-id="element-3924">
                <thead data-id="element-3925">
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/10" data-id="element-3926">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3927">Producto</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-3928">SKU</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-3929">Categoría</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3930">Precio</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3931">Restaurar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3932">
                  {archivedProducts.map(product => <tr key={product.id} className="hover:bg-amber-50/50 dark:hover:bg-amber-900/10 opacity-75" data-id="element-3933">
                      <td className="py-3 px-4" data-id="element-3934">
                        <p className="font-medium text-gray-700 dark:text-gray-300" data-id="element-3935">{product.name}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[200px]" data-id="element-3936">{product.description}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell" data-id="element-3937">{product.sku}</td>
                      <td className="py-3 px-4 hidden sm:table-cell" data-id="element-3938">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" data-id="element-3939">
                          <TagIcon className="w-3 h-3" data-id="element-3940" />
                          {product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400" data-id="element-3941">{formatCurrency(product.price)}</td>
                      <td className="py-3 px-4 text-right" data-id="element-3942">
                        <button onClick={() => handleUnarchiveProduct(product.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded-lg transition-colors" title="Restaurar al catálogo" data-id="element-3943">
                          <ArchiveRestoreIcon className="w-3.5 h-3.5" data-id="element-3944" />
                          Restaurar
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </Card>}

      {/* Product Modal */}
      <Modal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'} size="md" icon={<PackageIcon className="w-6 h-6" data-id="element-3946" />} accentColor="amber" data-id="element-3945">
        
        <form onSubmit={handleSaveProduct} className="space-y-6" data-id="element-3947">
          <FormSection title="Información Básica" data-id="element-3948">
            <div className="space-y-4" data-id="element-3949">
              <Input label="Nombre del Producto" value={productForm.name} onChange={e => setProductForm({
              ...productForm,
              name: e.target.value
            })} error={productErrors.name} data-id="element-3950" />
              

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3951">
                <Input label="SKU (Código)" value={productForm.sku} onChange={e => setProductForm({
                ...productForm,
                sku: e.target.value
              })} error={productErrors.sku} data-id="element-3952" />
                

                <Select label="Categoría" options={CATEGORIES} value={productForm.category} onChange={v => setProductForm({
                ...productForm,
                category: v
              })} data-id="element-3953" />
                
              </div>
              <Textarea label="Descripción" value={productForm.description} onChange={e => setProductForm({
              ...productForm,
              description: e.target.value
            })} rows={2} data-id="element-3954" />
              
            </div>
          </FormSection>

          <FormSection title="Inventario y Precios" data-id="element-3955">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3956">
              <Input type="number" label="Costo" value={productForm.cost} onChange={e => setProductForm({
              ...productForm,
              cost: e.target.value
            })} error={productErrors.cost} data-id="element-3957" />
              

              <Input type="number" label="Precio Venta" value={productForm.price} onChange={e => setProductForm({
              ...productForm,
              price: e.target.value
            })} error={productErrors.price} data-id="element-3958" />
              

              <Input type="number" label="Stock" value={productForm.stock} onChange={e => setProductForm({
              ...productForm,
              stock: e.target.value
            })} error={productErrors.stock} data-id="element-3959" />
              
            </div>
            <div className="mt-4" data-id="element-3960">
              <Select label="Estado" options={[{
              value: 'active',
              label: 'Activo'
            }, {
              value: 'inactive',
              label: 'Inactivo'
            }]} value={productForm.status} onChange={v => setProductForm({
              ...productForm,
              status: v as any
            })} data-id="element-3961" />
              
            </div>
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-3962">
            <Button variant="outline" type="button" onClick={() => setIsProductModalOpen(false)} data-id="element-3963">
              
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3964">
              {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Sale Modal */}
      <Modal isOpen={isSaleModalOpen} onClose={() => {
      setIsSaleModalOpen(false);
      setEditingSale(null);
    }} title={editingSale ? 'Editar Venta' : 'Registrar Venta'} size="lg" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-3966" />} accentColor="amber" data-id="element-3965">
        
        {editingSale && <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300" data-id="element-3967">
          <AlertTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-3968" />
          <span data-id="element-3969">Estás editando una venta existente. El stock se ajustará automáticamente. Este cambio quedará registrado en la bitácora.</span>
        </div>}
        <form onSubmit={handleSaveSale} className="space-y-6" data-id="element-3970">
          <FormSection title="Detalles de la Venta" data-id="element-3971">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3972">
              <Select label="Cliente / Paciente" options={patients.map(p => ({
              value: p.id,
              label: p.name
            }))} value={saleForm.patientId} onChange={v => setSaleForm({
              ...saleForm,
              patientId: v
            })} error={saleErrors.patientId} searchable data-id="element-3973" />
              

              <Select label="Vendedor" options={sellers.map(s => ({
              value: s.id,
              label: s.name
            }))} value={saleForm.sellerId} onChange={v => setSaleForm({
              ...saleForm,
              sellerId: v
            })} error={saleErrors.sellerId} searchable placeholder="Seleccionar vendedor..." data-id="element-3974" />
              

              <Input type="date" label="Fecha" value={saleForm.date} onChange={e => setSaleForm({
              ...saleForm,
              date: e.target.value
            })} error={saleErrors.date} data-id="element-3975" />
              
            </div>
          </FormSection>

          <FormSection title="Productos" data-id="element-3976">
            {saleErrors.items && <p className="text-sm text-red-500 mb-2" data-id="element-3977">{saleErrors.items}</p>}
            <div className="space-y-3" data-id="element-3978">
              {saleItems.map((item, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3979">
                
                  <div className="flex-1" data-id="element-3980">
                    <Select options={products.filter(p => p.status === 'active').map(p => ({
                  value: p.id,
                  label: `${p.name} (${p.stock} disp.) - ${formatCurrency(p.price)}`
                }))} value={item.productId} onChange={v => updateSaleItem(idx, 'productId', v)} searchable placeholder="Seleccionar producto..." data-id="element-3981" />
                  

                    {saleErrors[`item_${idx}`] && <p className="text-xs text-red-500 mt-1" data-id="element-3982">
                        {saleErrors[`item_${idx}`]}
                      </p>}
                  </div>
                  <div className="w-20" data-id="element-3983">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-3984">
                      Cant.
                    </label>
                    <Input type="number" min="1" value={item.quantity.toString()} onChange={e => updateSaleItem(idx, 'quantity', parseInt(e.target.value) || 1)} data-id="element-3985" />
                  
                  </div>
                  <div className="w-20" data-id="element-3986">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-3987">
                      Desc. %
                    </label>
                    <Input type="number" min="0" max="100" value={item.discount.toString()} onChange={e => updateSaleItem(idx, 'discount', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))} data-id="element-3988" />
                  
                  </div>
                  {item.productId && item.discount > 0 && <div className="flex flex-col justify-end pb-1" data-id="element-3989">
                      <span className="text-xs text-gray-400 line-through" data-id="element-3990">
                        {formatCurrency((products.find(p => p.id === item.productId)?.price || 0) * item.quantity)}
                      </span>
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-3991">
                        {formatCurrency((products.find(p => p.id === item.productId)?.price || 0) * item.quantity * (1 - item.discount / 100))}
                      </span>
                    </div>}
                  <button type="button" onClick={() => removeSaleItem(idx)} className="p-2 mt-1 text-red-500 hover:bg-red-50 rounded-lg" disabled={saleItems.length === 1} data-id="element-3992">
                  
                    <TrashIcon className="w-5 h-5" data-id="element-3993" />
                  </button>
                </div>)}
              <Button type="button" variant="outline" size="sm" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3995" />} onClick={addSaleItem} data-id="element-3994">
                
                Agregar otro producto
              </Button>
            </div>
            <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-between border border-orange-100 dark:border-orange-800" data-id="element-3996">
              <span className="text-orange-800 dark:text-orange-300 font-medium" data-id="element-3997">
                Total de la Venta:
              </span>
              <span className="text-2xl font-bold text-orange-600 dark:text-orange-400" data-id="element-3998">
                {formatCurrency(calculateSaleTotal())}
              </span>
            </div>
          </FormSection>

          <FormSection title="Notas Adicionales" data-id="element-3999">
            <Textarea value={saleForm.notes} onChange={e => setSaleForm({
            ...saleForm,
            notes: e.target.value
          })} rows={2} placeholder="Observaciones sobre la venta..." data-id="element-4000" />
            
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-4001">
            <Button variant="outline" type="button" onClick={() => {
            setIsSaleModalOpen(false);
            setEditingSale(null);
          }} data-id="element-4002">
              
              Cancelar
            </Button>
            <Button type="submit" leftIcon={<DollarSignIcon className="w-4 h-4" data-id="element-4004" />} data-id="element-4003">
              
              {editingSale ? 'Guardar Cambios' : 'Registrar Venta'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Archive Confirm Modal ─────────────────────────────────────── */}
      {archiveConfirmProduct && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-4005">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !archiveLoading && setArchiveConfirmProduct(null)} data-id="element-4006" />
          {/* Card */}
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4007">
            {/* Top accent bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" data-id="element-4008" />
            <div className="p-6" data-id="element-4009">
              {/* Icon + title */}
              <div className="flex items-start gap-4 mb-4" data-id="element-4010">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center" data-id="element-4011">
                  <ArchiveIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" data-id="element-4012" />
                </div>
                <div data-id="element-4013">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-4014">
                    ¿Archivar producto?
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-4015">
                    Esta acción no eliminará el producto permanentemente.
                  </p>
                </div>
              </div>

              {/* Product info */}
              <div className="mb-5 p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" data-id="element-4016">
                <div className="flex items-center gap-3" data-id="element-4017">
                  <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center" data-id="element-4018">
                    <PackageIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" data-id="element-4019" />
                  </div>
                  <div data-id="element-4020">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-4021">
                      {archiveConfirmProduct.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4022">
                      SKU: {archiveConfirmProduct.sku} · {archiveConfirmProduct.category}
                    </p>
                  </div>
                </div>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2 p-3 mb-6 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300" data-id="element-4023">
                <ArchiveRestoreIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-4024" />
                <span data-id="element-4025">Podrás restaurarlo en cualquier momento desde la pestaña <strong data-id="element-4026">Productos Archivados</strong>.</span>
              </div>

              {/* Actions */}
              <div className="flex gap-3" data-id="element-4027">
                <button onClick={() => setArchiveConfirmProduct(null)} disabled={archiveLoading} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50" data-id="element-4028">
                  Cancelar
                </button>
                <button onClick={confirmArchiveProduct} disabled={archiveLoading} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold shadow-md shadow-orange-200 dark:shadow-orange-900/30 transition-all disabled:opacity-70 flex items-center justify-center gap-2" data-id="element-4029">
                  {archiveLoading ? <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" data-id="element-4030">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4031" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" data-id="element-4032" />
                      </svg>
                      Archivando...
                    </> : <>
                      <ArchiveIcon className="w-4 h-4" data-id="element-4033" />
                      Sí, archivar
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}